import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import FileAppResource from './FileAppResource';
import WorkspaceTypeAppResource from './WorkspaceTypeAppResource';
import LuFormCategoryAppResource from "./LuFormCategoryAppResource";
import LuCountryAppResource from "./LuCountryAppResource";
import LuStateAppResource from "./LuStateAppResource";
import LuESignatureMethodAppResource from "./LuESignatureMethodAppResource";
import LuFormLicenceStatusAppResource from "./LuFormLicenceStatusAppResource";
import FormAuthorAppResource from "./FormAuthorAppResource";
import LuOrganizationOccupationAppResource from "./LuOrganizationOccupationAppResource";
import FormsetAppResource from "./FormsetAppResource";

export const BROKER_SIGNATURE = 'broker_signature';
export const BROKER_ASSOCIATE_SIGNATURE = 'broker_associate_signature';
export const PRIMARY_CLIENT_SIGNATURE = 'primary_client_signature';
export const SECONDARY_CLIENT_SIGNATURE = 'secondary_client_signature';
export const BROKER_NAME = 'broker_name';
export const BROKER_ASSOCIATE_NAME = 'broker_associate_name';
export const PRIMARY_CLIENT_NAME = 'primary_client_name';
export const SECONDARY_CLIENT_NAME = 'secondary_client_name';
export const BROKER_INITIALS = 'broker_initials';
export const BROKER_ASSOCIATE_INITIALS = 'broker_associate_initials';
export const PRIMARY_CLIENT_INITIALS = 'primary_client_initials';
export const SECONDARY_CLIENT_INITIALS = 'secondary_client_initials';
export const BROKER_DATE = 'broker_date';
export const BROKER_ASSOCIATE_DATE = 'broker_associate_date';
export const PRIMARY_CLIENT_DATE = 'primary_client_date';
export const SECONDARY_CLIENT_DATE = 'secondary_client_date';
export const BROKER_LICENSE = 'broker_license';
export const BROKER_CHECKBOX = 'broker_checkbox';
export const BROKER_ASSOCIATE_CHECKBOX = 'broker_associate_checkbox';

export const SIGNATURE_TYPE_OPTIONS = {
  'broker_signature': 'Broker Signature',
  'broker_associate_signature': 'Broker Associate Signature',
  'primary_client_signature': 'Primary Client Signature',
  'secondary_client_signature': 'Secondary Client Signature',
  'broker_name': 'Broker Name',
  'broker_associate_name': 'Broker Associate Name',
  'primary_client_name': 'Primary Client Name',
  'secondary_client_name': 'Secondary Client Name',
  'broker_initials': 'Broker Initials',
  'broker_associate_initials': 'Broker Associate Initials',
  'primary_client_initials': 'Primary Client Initials',
  'secondary_client_initials': 'Secondary Client Initials',
  'broker_date': 'Broker Date',
  'broker_associate_date': 'Broker Associate Date',
  'primary_client_date': 'Primary Client Date',
  'secondary_client_date': 'Secondary Client Date',
  'broker_license': 'Broker License',
  'broker_checkbox': 'Broker Checkbox',
  'broker_associate_checkbox': 'Broker Associate Checkbox',
};

class FormAppResource extends AppResource {

  /**
   * @returns {string}
   */
  appResourceType() {
    return 'form';
  }

  /**
   * @returns {string}
   */
  appResourceApiPrefix() {
    return 'forms';
  }

  /**
   * @returns {*[]}
   */
  hidden() {
    return super.hidden().concat([
      'uuid',
      'deleted_at',
    ]);
  }

  /**
   * @returns {*[]}
   */
  fillable() {
    return [
      'formCategoryId',
      'countryId',
      'stateId',
      'eSignatureMethodId',
      'formLicenceStatusId',
      'previewFileUuid',
      'formNumber',
      'name',
      'officialName',
      'identifier',
      'publicationDate',
      'description',
      'isActive',
      'hasESignature',
      'allowEdit',
      'generatePdf',
      'hasProjectInteractions',
      'addToProjectForms',
      'restrictedUse',
      'licenceExpirationDate',
      'jotformId',
      'jotformPath',
      'previewFile',
      'projectTypes',
      'formAuthorUuid',
      'formCompleterRoles',
      'formsets',
      'meta',
      'issuedByAnAuthority',
    ];
  }

  /**
   * @returns {*[]}
   */
  relationMap() {
    return {
      formCategory: LuFormCategoryAppResource,
      country: LuCountryAppResource,
      state: LuStateAppResource,
      eSignatureMethod: LuESignatureMethodAppResource,
      formLicenceStatus: LuFormLicenceStatusAppResource,
      previewFile: FileAppResource,
      projectTypes: WorkspaceTypeAppResource,
      formAuthor: FormAuthorAppResource,
      formCompleterRoles: LuOrganizationOccupationAppResource,
      formsets: FormsetAppResource,
    };
  }

  /**
   * @returns {*[]}
   */
  relationForeignKeys() {
    return {
      formCategory: 'form_category_id',
      country: 'country_id',
      state: 'state_id',
      eSignatureMethod: 'e_signature_method_id',
      formLicenceStatus: 'form_licence_status_id',
      previewFile: 'preview_file_uuid',
      formAuthor: 'form_author_uuid',
    };
  }

  /**
   * @returns {*}
   */
  validationRules() {
    return {
      formCategoryId: {
        numericality: true,
        presence: {
          message: "^Form category can't be blank."
        },
      },
      countryId: {
        numericality: true,
        presence: {
          message: "^Country can't be blank."
        },
      },
      stateId: {
        numericality: true,
      },
      eSignatureMethodId: {
        numericality: true,
      },
      formLicenceStatusId: {
        numericality: true,
      },
      previewFileUuid: {
        type: 'string',
      },
      formNumber: {
        type: 'string',
        length: {
          maximum: 255,
        },
      },
      name: {
        type: 'string',
        presence: {
          message: "^Form name can't be blank."
        },
        length: {
          maximum: 255,
        },
      },
      officialName: {
        type: 'string',
        length: {
          maximum: 255,
        },
      },
      identifier: {
        type: 'string',
        length: {
          maximum: 255,
        },
      },
      publicationDate: {
        datetime: true,
      },
      licenceExpirationDate: {
        datetime: {
          dateOnly: true
        },
      },
      description: {
        type: 'string',
        length: {
          maximum: 65535,
        },
      },
      isActive: {
        type: 'boolean',
        presence: {
          message: "^Form status can't be blank."
        },
      },
      hasESignature: {
        type: 'boolean',
      },
      allowEdit: {
        type: 'boolean',
      },
      generatePdf: {
        type: 'boolean',
      },
      hasProjectInteractions: {
        type: 'boolean',
      },
      addToProjectForms: {
        type: 'boolean',
      },
      restrictedUse: {
        type: 'boolean',
      },
      jotformId: {
        type: 'string',
        presence: {
          message: "^JotForm ID can't be blank."
        },
        length: {
          maximum: 255,
        },
      },
      jotformPath: {
        type: 'string',
        presence: {
          message: "^JotForm path can't be blank."
        },
        length: {
          maximum: 255,
        },
      },
      projectTypes: {
        type: 'array',
      },
      formAuthorUuid: {
        type: 'string',
        presence: {
          message: "^Form author can't be blank."
        },
      },
      formCompleterRoles: {
        type: 'array',
      },
      meta: {
      },
      issuedByAnAuthority: {
        type: 'boolean',
      },
    };
  }

}

export default FormAppResource;
